import axios from 'axios';
import { withPrefix } from 'gatsby';
import { UserSubscriptionViewModel } from '../store/subscriptions/types';
import { UsageLogEntry } from '../model/analytics/types';
import { SubscriptionData, ComponentPreference } from '@ecri/react-components';
import {
  UserContactViewModel,
  TrustedDevice,
  LoginHistoryEntry,
  UpdatePasswordRequest,
  UpdatePasswordResponse,
} from '../model/profile/types';
import { SavedItemViewModel, SavedItemType } from '../model/saveditems/types';
import { AnnouncementCollectionViewModel } from '../store/announcement/types';
import { generateUUID } from '../util/uuid';
import { UserInputViewModel } from '../model/elearnprofile/types';
import { MfaOptionsViewModel } from '../model/mfa/types';
import { AddressTypesViewModel } from '../store/addresstypes/types';
import { LMSUserViewModel } from '../store/selfregistration/types';
import { AreSeatsAvailableModel, AreSeatsAvailableResult, NewUserVerificationModel, VerifyLmsUserResult } from '../store/selfregistration/types';
import { NewUnifiedUserVerificationModel, UserViewModel, VerifyUserResult } from '../store/register/types';

axios.defaults.headers.common['Content-type'] = 'application/json';
axios.defaults.headers.common['x-correlation-id'] = generateUUID();

const apiBaseURL = `${process.env.GATSBY_API_URL || ''}${withPrefix('/api')}`;

export const changePassword = (
  token: string,
  updatePasswordRequest: UpdatePasswordRequest
) => {
  return axios.post<UpdatePasswordResponse>(
    `${apiBaseURL}/user/changepassword`,
    updatePasswordRequest,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
};

export const getDaysLastPasswordChange = (token: string) => {
  return axios.get(`${apiBaseURL}/user/dayssincelastpasswordchange`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const getTotalTrustedDevices = (token: string) =>
  axios.get<number>(`${apiBaseURL}/user/devicetrust/count`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const fetchTrustedDevices = (
  token: string,
  pageSize: number,
  offset: number
) => {
  return axios.get<TrustedDevice[]>(
    `${apiBaseURL}/user/devicetrust?pageSize=${pageSize}&offset=${offset}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
};

export const getTotalLogins = (token: string) =>
  axios.get<number>(`${apiBaseURL}/user/loginhistorycount`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const fetchLoginHistory = (
  token: string,
  pageSize: number,
  offset: number
) => {
  return axios.get<LoginHistoryEntry[]>(
    `${apiBaseURL}/user/loginhistory?pageSize=${pageSize}&offset=${offset}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
};

export const removeTrustedDevice = (token: string, id: number) => {
  return axios.delete(`${apiBaseURL}/user/devicetrust/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const removeAllTrustedDevices = (token: string) => {
  return axios.post(
    `${apiBaseURL}/user/devicetrust/removeall`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
};

export const loadAllSubscriptions = () =>
  axios.get<SubscriptionData>(`${apiBaseURL}/general/subscriptioninfo`);

export const loadUserSubscriptions = (token: string) =>
  axios.get<UserSubscriptionViewModel>(`${apiBaseURL}/user/subscriptions`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const getUserProfileByProfileId = (profileId: string) =>
  axios.get<UserContactViewModel>(
    `${apiBaseURL}/user/GetUserProfileByProfileIdAsync?profileId=${profileId}`
  );

export const getUserProfileByEmail = (email: string) =>
  axios.get<UserContactViewModel>(
    `${apiBaseURL}/user/GetUserProfileAsync?email=${email}`
  );


export const loadAddressTypes = () =>
  axios.get<AddressTypesViewModel[]>(
    `${apiBaseURL}/user/GetAddressTypesAsync`
  );

export const registerLMSUser = (lmsUser: LMSUserViewModel) =>
  axios.post(`${apiBaseURL}/user/RegisterLMSUserAsync`, lmsUser);

export const verifyLmsRegistration = (lmsUser: NewUserVerificationModel) =>
  axios.post<VerifyLmsUserResult, any>(`${apiBaseURL}/user/VerifyLMSUserAsync`, lmsUser);

export const areSeatsAvailable = (seatsAvailable: AreSeatsAvailableModel) =>
  axios.post<AreSeatsAvailableResult, any>(`${apiBaseURL}/user/AreSeatsAvailableAsync`, seatsAvailable);

export const decryptOrder = (encryptedOrder: string) =>
  axios.get<string>(
    `${apiBaseURL}/cipher/getdecrypt/${encryptedOrder}`
  );

export const encryptProfile = (encryptProfile: string) =>
  axios.get<string>(
    `${apiBaseURL}/cipher/getencrypt/${encryptProfile}`
  );


export const loadProfileMemberships = (orderNumber: string) =>
  axios.get<string[]>(`${apiBaseURL}/user/getprofilemembershipsasync?ordernumber=${orderNumber}`);

export const getUserProfile = (profileId: string) =>
  axios.get<string[]>(`${apiBaseURL}/user/getprofilebycrmidasync?profileId=${profileId}`);


export const updateUserPreferences = (
  token: string,
  updates: ComponentPreference[]
) =>
  axios.post(`${apiBaseURL}/user/preferences/display`, updates, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const updateUserContactInfo = (
  token: string,
  contactInfo: UserContactViewModel
) =>
  axios.post<UserContactViewModel, any>(
    `${apiBaseURL}/user/contact/updateprofile`,
    contactInfo,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

export const updateUserDeviceTrustParticipationStatus = (
  token: string,
  status: boolean
) =>
  axios.post(
    `${apiBaseURL}/user/preferences/devicetrust`,
    { participate: status },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

export const loadReportAndRequestInfo = (token: string) =>
  axios.get(`${apiBaseURL}/user/content/reportandrequestinfo`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const loadAllSavedItems = (token: string) => {
  return axios.get(`${apiBaseURL}/user/saveditems`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const loadTrendingTopics = (token: string) => {
  return axios.get(`${apiBaseURL}/user/content/trendingtopics`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const saveTrendingTopic = (token: string, termSetMapId: number) => {
  return axios.put(
    `${apiBaseURL}/user/saveditems/savetopic/${termSetMapId}`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
};

const buildDeleteEndpoint = (type: SavedItemType) => {
  switch (type) {
    case SavedItemType.Documents:
      return 'removedocuments';
    case SavedItemType.Searches:
      return 'removesearches';
    case SavedItemType.Topics:
      return 'removetopics';
  }
};

export const removeSavedItems = (
  token: string,
  type: SavedItemType,
  items: SavedItemViewModel[]
) => {
  const endpoint = `${apiBaseURL}/user/saveditems/${buildDeleteEndpoint(type)}`;
  return axios.post(
    endpoint,
    items.map((x) => ({ id: x.id })),
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
};

export const loadAnnouncements = (token: string) =>
  axios.get<AnnouncementCollectionViewModel>(`${apiBaseURL}/announcement`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const logToInternalAnalytics = (
  token: string,
  logEntry: UsageLogEntry
) =>
  axios
    .post(`${apiBaseURL}/analytics`, logEntry, {
      //timeout: 2500,
      headers: { Authorization: `Bearer ${token}` },
    })
    .catch((e) => console.error(e));

export const getAutoCompleteSuggestions = (phrase: string) =>
  axios
    .get(
      `${apiBaseURL}/general/searchsuggestions?searchPhrase=${encodeURIComponent(
        phrase
      )}`
    )
    .then((x) => x.data as string[])
    .catch((err) => {
      console.error(err);
      return [] as string[];
    });

export const updateElearnProfile = (
  token: string,
  request: UserInputViewModel
) => {
  return axios.post(`${apiBaseURL}/elearnprofile`, request, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const getElearnProfile = (token: string) => {
  return axios.get<UserInputViewModel>(`${apiBaseURL}/elearnprofile`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const fetchMfaSettings = (token: string) => {
  return axios.get(`${apiBaseURL}/mfasettings`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const updateMfaSettings = (
  token: string,
  settings: MfaOptionsViewModel
) => {
  return axios.post(`${apiBaseURL}/mfasettings`, settings, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const sendSmsVerification = (token: string, phone: string) => {
  return axios.put(
    `${apiBaseURL}/mfasettings/sendsmsvalidationcode`,
    {
      phone,
    },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
};

export const validateSmsPhone = (
  token: string,
  sequenceNumber: number,
  phone: string,
  code: string
) => {
  return axios.put(
    `${apiBaseURL}/mfasettings/validatephone`,
    {
      phone,
      code,
      sequenceNumber,
    },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
};

export const registerUnifiedUser = (unifiedUser: UserViewModel) =>
  axios.post(`${apiBaseURL}/user/RegisterUnifiedUserAsync`, unifiedUser);

export const verifyUnifiedRegistration = (unifiedUser: NewUnifiedUserVerificationModel) =>
  axios.post<VerifyUserResult, any>(`${apiBaseURL}/user/VerifyUnifiedUserAsync`, unifiedUser);

export const decryptProfileId = (encryptedProfileId: string) =>
  axios.get<string>(
    `${apiBaseURL}/cipher/getdecrypt/${encryptedProfileId}`
  );